/**
 * O cartão do site entra (ou muda de altura) sem tirar a pessoa do lugar na lista que ela lê
 * (desenho §3, spec §3.5). A decisão é a função pura `decidirAjuste` (`lib/links/entradaSemPulo`);
 * este hook só mede o rolável e aplica.
 *
 * Roda antes de pintar (`useLayoutEffect`) e a cada mudança de tamanho do cartão (`ResizeObserver`:
 * imagem que falha, fonte que chega). O `delta` é a diferença para a altura anterior, guardada num `ref`
 * (na montagem a anterior é 0, então o delta é a altura inteira do cartão novo). O ajuste é instantâneo:
 * atribui `scrollTop`, sem `smooth`, sem `scrollTo` e sem `scrollIntoView` (a armadilha de 29/09).
 * Sem `ResizeObserver` (ambiente antigo, happy-dom) só o passo de montagem roda.
 *
 * 🔴 O `ref` da altura NÃO volta a 0 na limpeza do efeito: no StrictMode o efeito roda, desfaz e roda de
 * novo, e a segunda rodada somaria o `delta` outra vez.
 *
 * 🔴 Dentro de uma lista GRUDADA no pé (`useGrudadoNoPe`, T-R19 de 28/10) o pé é dela: grudada, ela vai ao fim exato
 * pelo observador do conteúdo e o cartão não escreve; com o salto rolando, o salto manda. Solta, o cartão só faz a
 * regra 2 (sem âncora do navegador, cartão acima do topo visível), e PELA lista (`compensar`): a âncora própria dela
 * corrige depois o que a altura do cartão não conta (a margem: 237,23 × 243,23 px; sozinha, a regra 2 deixava a leitura
 * descer 81 px com 13 cartões acima, medido no Chromium sem âncora). A conta por cartão não fechava o pé: N cartões no
 * mesmo commit não se enxergam. Fora de uma lista grudada (o fio, o feed) valem as três regras, com a regra 1 só para o
 * cartão abaixo do topo visível.
 */
import { useLayoutEffect, useRef, type RefObject } from "react";

import { decidirAjuste, navegadorAncoraRolagem, rolavelDe } from "@/lib/links/entradaSemPulo";
import { listaGrudadaDe } from "@/lib/rolagem/grudadoNoPe";

function aplicarEntrada(cartao: HTMLElement, delta: number): void {
  const lista = listaGrudadaDe(cartao);
  // Grudada, o pé é da lista; com o salto rolando, o salto manda.
  if (lista && (lista.grudado || lista.suspenso)) return;
  const rolavel = lista ? lista.rolavel : rolavelDe(cartao);
  // Sem ancestral que rola, quem rola é a janela (o feed): mede-se o document.scrollingElement, e o topo
  // da área visível é 0 (o getBoundingClientRect() da raiz anda junto com a rolagem).
  const alvo = (rolavel ?? document.scrollingElement ?? document.documentElement) as HTMLElement;
  const topoDoCartao = cartao.getBoundingClientRect().top;
  const topoDoRolavel = rolavel ? rolavel.getBoundingClientRect().top : 0;
  const ajuste = decidirAjuste({
    delta,
    scrollTop: alvo.scrollTop,
    scrollHeight: alvo.scrollHeight,
    clientHeight: alvo.clientHeight,
    topoDoCartao,
    topoDoRolavel,
    navegadorAncora: navegadorAncoraRolagem(rolavel),
    // Na lista grudada o pé é dela. Fora dela, o cartão que cresce acima do que se lê não aumenta a distância ao pé
    // (a âncora do navegador ou a regra 2 já o compensam), e a conta do delta puxaria ao pé quem lê perto dele.
    regraDoPe: !lista && topoDoCartao > topoDoRolavel,
  });
  if (!ajuste) return;
  // Na lista grudada a regra 2 passa por ela: a escrita não conta como a pessoa rolando, e a âncora própria corrige o resto.
  if (lista) lista.compensar(ajuste.scrollTop - alvo.scrollTop);
  else alvo.scrollTop = ajuste.scrollTop;
}

export function useEntradaSemPulo(ref: RefObject<HTMLElement | null>): void {
  const alturaAnterior = useRef(0);

  useLayoutEffect(() => {
    const cartao = ref.current;
    if (!cartao) return;

    const medirEAplicar = () => {
      const altura = cartao.getBoundingClientRect().height;
      const delta = altura - alturaAnterior.current;
      alturaAnterior.current = altura;
      // Altura igual: nem mede o rolável (a primeira notificação do observador cai aqui).
      if (delta === 0) return;
      aplicarEntrada(cartao, delta);
    };

    medirEAplicar();

    const observador =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(medirEAplicar);
    observador?.observe(cartao);
    return () => observador?.disconnect();
  }, [ref]);
}
